/**
 * Builds the Event Time Line for one monitoring location from the chart
 * response. Nothing is stored: every event is read from data the location's
 * chart already loads (pour record, logger status, targets, limits and the
 * temperature readings).
 *
 * Rules:
 *  - An event only counts as done when its time has passed. Dates typed into
 *    pour setup that are still in the future are "scheduled", never ticked.
 *  - Done events are in time order; scheduled then pending events follow.
 *  - Limit exceedances are worked out from the readings against this
 *    location's temperature limits, one event per breach.
 *  - Reached targets and the grade say how long after the concrete start
 *    they came (else the monitoring start), as the Targets card.
 */
import { parseUtc } from '../../utils/datetime';
import {
  ARRHENIUS,
  NURSE_SAUL,
  TARGET_OUTSIDE_RANGE_LABEL,
  TTF_UNIT,
  describeMaturity,
  formatMaturityWithIndex,
  isTargetOutsideRange,
  maturityMeta,
  targetStrengthText,
} from '../../utils/maturityDisplay';
import { elapsedSinceStart, formatElapsed } from '../../utils/targetStatus';
import type { ChartDataResponse, ChartLimit } from '../../services/pours';

// formatElapsed lives with the target status text; re-exported for older
// imports.
export { formatElapsed };

export type TimelineCategory = 'Admin' | 'Temperature' | 'Strength';
/** done = happened; alert = a limit breach; scheduled = planned time not yet
 *  reached; pending = no time known yet (e.g. a strength target). */
export type TimelineStatus = 'done' | 'alert' | 'scheduled' | 'pending';

export interface TimelineEvent {
  key: string;
  label: string;
  category: TimelineCategory;
  status: TimelineStatus;
  /** Epoch ms; undefined for pending events. */
  time?: number;
  /** Secondary lines under the label. */
  details: string[];
}

export interface TimelineEvents {
  /** Done and alert events, oldest first. */
  past: TimelineEvent[];
  /** Scheduled (by time) then pending events. */
  future: TimelineEvent[];
}

export interface TimelineInput {
  chartData: ChartDataResponse;
  /** Ambient / site-hardware view: no concrete or strength events. */
  ambient: boolean;
  /** Location runs maturity: show Maturity Start and Grade Strength. */
  showStrength: boolean;
  /** Fallback units; the chart response's own maturity descriptor and
   *  company strength unit win when it sends them. */
  maturityUnit: string;
  strengthUnit: string;
  /** Formats an epoch ms in the pour's timezone. */
  formatTime: (ms: number) => string;
  now?: number;
}

/** Readings back inside the limit for less than this are treated as the same
 *  breach, so sensor noise around the threshold doesn't list dozens. */
const BREACH_MERGE_GAP_MS = 60 * 60 * 1000;

const toMs = (value?: string | null): number | undefined => {
  if (!value) return undefined;
  const ms = parseUtc(value).getTime();
  return Number.isNaN(ms) ? undefined : ms;
};

const isBreach = (value: number, limit: ChartLimit): boolean => {
  switch (limit.operator) {
    case '>': return value > limit.value;
    case '>=': return value >= limit.value;
    case '<': return value < limit.value;
    case '<=': return value <= limit.value;
    default: return false;
  }
};

interface Breach {
  start: number;
  end?: number; // first reading back within the limit
  peak: number;
}

/** Breach periods for one limit over time-ordered [ms, value] readings. */
export const findBreaches = (readings: (number | null)[][], limit: ChartLimit): Breach[] => {
  const lower = limit.operator.startsWith('<');
  const breaches: Breach[] = [];
  let open: Breach | null = null;
  for (const point of readings) {
    const [t, v] = point;
    if (t == null || v == null) continue;
    if (isBreach(v, limit)) {
      const last = breaches[breaches.length - 1];
      if (!open && last && last.end != null && t - last.end < BREACH_MERGE_GAP_MS) {
        // Back outside the limit shortly after recovering: same breach.
        open = last;
        open.end = undefined;
      } else if (!open) {
        open = { start: t, peak: v };
        breaches.push(open);
      }
      open.peak = lower ? Math.min(open.peak, v) : Math.max(open.peak, v);
    } else if (open) {
      open.end = t;
      open = null;
    }
  }
  return breaches;
};

export const buildTimelineEvents = ({
  chartData,
  ambient,
  showStrength,
  maturityUnit,
  strengthUnit,
  formatTime,
  now = Date.now(),
}: TimelineInput): TimelineEvents => {
  const events: (TimelineEvent & { order: number })[] = [];
  let order = 0;
  const add = (e: TimelineEvent) => events.push({ ...e, order: order++ });
  const loc = chartData.location_details;
  const complete = chartData.pour_status === '2';

  /** A dated event: done once its time has passed, else scheduled. */
  const dated = (key: string, label: string, category: TimelineCategory, iso?: string | null, details: string[] = []) => {
    const time = toMs(iso);
    if (time == null) return;
    add({ key, label, category, time, details, status: time <= now ? 'done' : 'scheduled' });
  };

  // ---- Admin -------------------------------------------------------------
  dated(
    'created',
    ambient ? 'Created' : 'Pour Created',
    'Admin',
    chartData.pour_created,
    chartData.pour_created_by ? [`By ${chartData.pour_created_by}`] : [],
  );
  dated('pour-start', ambient ? 'Start Time' : 'Pour Start Time', 'Admin', chartData.pour_start_time);

  const monitoringLabel = ambient ? 'Monitoring Start' : 'Concrete Monitoring Start';
  const monitoringStart = toMs(loc?.logging_started);
  if (monitoringStart != null) {
    add({ key: 'monitoring-start', label: monitoringLabel, category: 'Admin', status: 'done', time: monitoringStart, details: [] });
  } else if (!complete) {
    // Not started yet: show the planned start, even if it has passed.
    const planned = toMs(loc?.scheduled_start);
    if (planned != null) {
      add({
        key: 'monitoring-start',
        label: monitoringLabel,
        category: 'Admin',
        status: 'scheduled',
        time: planned,
        details: planned <= now ? ['Logger has not started yet'] : [],
      });
    }
  }

  if (showStrength && !ambient) {
    dated('maturity-start', 'Maturity Start', 'Admin', loc?.ea_started);
  }

  const stopLabel = ambient ? 'Monitoring Stop' : 'Concrete Monitoring Stop';
  const stopped = toMs(loc?.logging_finished);
  if (stopped != null) {
    add({ key: 'monitoring-stop', label: stopLabel, category: 'Admin', status: 'done', time: stopped, details: [] });
  } else if (!complete) {
    const planned = toMs(chartData.planned_stop);
    if (planned != null) {
      add({ key: 'monitoring-stop', label: stopLabel, category: 'Admin', status: 'scheduled', time: planned, details: [] });
    }
  }

  if (complete) {
    // vwPourInfo returns 'System' / 'Manual'; PourInfo stores '1' / '3'.
    const stopBy =
      ['1', 'System'].includes(chartData.pour_stop_by ?? '') ? 'Stopped automatically'
        : ['3', 'Manual'].includes(chartData.pour_stop_by ?? '') ? 'Stopped manually'
          : undefined;
    dated('finalised', ambient ? 'Finalised' : 'Pour Finalised', 'Admin', chartData.pour_stopped, stopBy ? [stopBy] : []);
  }

  // ---- Temperature: one event per limit breach ----------------------------
  const readings = [...(chartData.temp ?? [])].sort((a, b) => (a[0] ?? 0) - (b[0] ?? 0));
  const lastReading = readings.length > 0 ? readings[readings.length - 1][0] : undefined;
  (chartData.limit ?? []).forEach((limit, li) => {
    const lower = limit.operator.startsWith('<');
    const limitText = `${limit.operator} ${limit.value}°C`;
    const label = limit.name
      ? `Temperature Limit ${limit.name} Exceeded`
      : `Temperature Limit ${limitText} Exceeded`;
    findBreaches(readings, limit).forEach((b, bi) => {
      const details = [
        `${limit.name ? `${limitText}, ` : ''}${lower ? 'lowest' : 'peak'} ${b.peak.toFixed(1)}°C`,
      ];
      if (b.end != null) {
        details.push(`Back within limit ${formatTime(b.end)} (${formatElapsed(b.end - b.start)})`);
      } else if (complete) {
        details.push('Outside limit until monitoring stopped');
      } else {
        details.push(
          lastReading != null
            ? `Still outside limit at last reading (${formatElapsed(lastReading - b.start)})`
            : 'Still outside limit',
        );
      }
      add({ key: `limit-${li}-${bi}`, label, category: 'Temperature', status: 'alert', time: b.start, details });
    });
  });

  // ---- Strength ----------------------------------------------------------
  if (!ambient) {
    const targets = chartData.target ?? [];
    // Targets and the grade are measured from the concrete start (maturity
    // counted from), as legacy and the Targets card, else from the
    // monitoring start.
    const concreteStart = loc?.ea_started ?? chartData.ea_start;
    const elapsed = (time: number): string | null =>
      elapsedSinceStart(time, concreteStart, loc?.logging_started);
    // The company strength unit and the pour's maturity unit, as the
    // response names them (older responses: the caller's).
    const sUnit = chartData.strength_unit?.trim() || strengthUnit;
    // The pour's maturity naming: the response's descriptor, else the
    // backend rule from the caller's unit (only °C·hrs is a TTF; anything
    // else is an equivalent age, at 20 °C when the response does not say).
    const mMeta = chartData.maturity
      ? maturityMeta(chartData)
      : describeMaturity(maturityUnit === TTF_UNIT ? NURSE_SAUL : ARRHENIUS);
    // 1 dp half up; an equivalent age names its specified temperature.
    const describe = (strength: number, ea: number): string | undefined =>
      strength > 0 ? targetStrengthText(strength, sUnit)
        : ea > 0 ? formatMaturityWithIndex(ea, mMeta, 1)
          : undefined;

    targets
      .slice()
      .sort((a, b) => a.target_ea - b.target_ea || a.target_strength - b.target_strength)
      .forEach((t) => {
        const target = describe(t.target_strength, t.target_ea);
        const label = t.target_label?.trim() || (target ? `Target ${target}` : 'Strength Target');
        const details = t.target_label && target ? [target] : [];
        if (isTargetOutsideRange(t, chartData.calibrated_range)) details.push(TARGET_OUTSIDE_RANGE_LABEL);
        const time = toMs(t.achievement_time);
        if (t.achieved && time != null) {
          const since = elapsed(time);
          if (since) details.push(since);
          add({ key: `target-${t.target_id}`, label, category: 'Strength', status: 'done', time, details });
        } else if (t.achieved) {
          add({ key: `target-${t.target_id}`, label, category: 'Strength', status: 'done', details: [...details, 'Reached (time not recorded)'] });
        } else {
          add({ key: `target-${t.target_id}`, label, category: 'Strength', status: 'pending', details });
        }
      });

    // Grade strength: only when the location can estimate strength (a usable
    // curve with included calibration samples) and no configured target
    // already covers it. Newer responses say when the location's maturity
    // reached the grade (grade_strength_time, null while not reached or when
    // the grade is outside the tested range); the plotted strength is blank
    // outside the calibration's plot window, so it is only scanned for older
    // responses.
    const grade = chartData.product_strength_grade;
    const strengthPoints = (chartData.ea ?? []).filter((p) => p[1] != null);
    const sentTime = chartData.grade_strength_time !== undefined;
    const range = chartData.calibrated_range;
    const hasCurve = range
      ? range.has_curve && range.has_samples
      : (sentTime && chartData.grade_strength_time != null) || strengthPoints.length > 0;
    const coveredByTarget = targets.some(
      (t) => /grade/i.test(t.target_label ?? '') || (grade != null && Math.abs(t.target_strength - grade) < 0.05),
    );
    if (showStrength && grade != null && grade > 0 && hasCurve && !coveredByTarget) {
      const hitTime = sentTime
        ? toMs(chartData.grade_strength_time)
        : (strengthPoints.find((p) => (p[1] as number) >= grade)?.[0] ?? undefined);
      const details = [`${grade.toFixed(1)} ${sUnit}`];
      if (chartData.grade_outside_range || isTargetOutsideRange({ target_strength: grade }, range)) {
        details.push(TARGET_OUTSIDE_RANGE_LABEL);
      }
      if (hitTime != null) {
        const since = elapsed(hitTime);
        if (since) details.push(since);
        add({ key: 'grade-strength', label: 'Grade Strength', category: 'Strength', status: 'done', time: hitTime, details });
      } else {
        add({ key: 'grade-strength', label: 'Grade Strength', category: 'Strength', status: 'pending', details });
      }
    }
  }

  const byTime = (a: TimelineEvent & { order: number }, b: TimelineEvent & { order: number }) =>
    (a.time ?? Infinity) - (b.time ?? Infinity) || a.order - b.order;
  const strip = ({ order: _o, ...e }: TimelineEvent & { order: number }): TimelineEvent => e;

  return {
    past: events.filter((e) => e.status === 'done' || e.status === 'alert').sort(byTime).map(strip),
    future: [
      ...events.filter((e) => e.status === 'scheduled').sort(byTime),
      ...events.filter((e) => e.status === 'pending'),
    ].map(strip),
  };
};
